import type {
  ProductOption,
  StoreProduct,
} from "../../../stores/useReservationStore";

/** One cart line: a product option held either by a ticket or by the order. */
export interface ProductLine {
  product: StoreProduct;
  option: ProductOption;
  ticketId: number | null; // null => per-order
  qty: number;
}

/**
 * A ticket holding less of a product than its minimum requires, with somewhere
 * left to go: emitted only when the shortfall is the customer's to fix.
 */
export interface MinimumViolation {
  product: StoreProduct;
  ticketId: number;
  required: number;
  held: number;
}

/**
 * Order-wide ceiling for an option: the lower of maximumOrder and stock.
 * Per-order products only — for per-ticket products the two halves of this
 * number mean different things (see getStockHeadroom / getTicketCeiling).
 */
export function getMaxAllowedQty(option: ProductOption | undefined): number {
  if (!option) return 0;
  return option.maximumOrder > 0
    ? Math.min(option.maximumOrder, option.quantity)
    : option.quantity;
}

/** Smallest quantity an option may be bought in, within one scope. */
export function getMinQty(option: ProductOption | undefined): number {
  return option?.minimumOrder > 0 ? option.minimumOrder : 1;
}

/**
 * THE shared cart model — one entry per (product, option, ticket|null) with
 * qty > 0. Every consumer that needs to walk the cart uses this and nothing
 * else; the two-quantity-maps shape on StoreProduct is not enforceable by the
 * type system, so hand-rolled flatten chains are how per-ticket products end up
 * silently priced at zero.
 */
export function getProductLines(products: StoreProduct[]): ProductLine[] {
  const lines: ProductLine[] = [];

  for (const product of products) {
    const optionMap = new Map(product.productOptions.map((o) => [o.id, o]));

    if (product.isPerTicket) {
      for (const [ticketKey, optionQuantities] of Object.entries(
        product.ticketOptionQuantities ?? {},
      )) {
        for (const [optionKey, qty] of Object.entries(optionQuantities)) {
          const option = optionMap.get(Number(optionKey));
          if (!option || qty <= 0) continue;
          lines.push({ product, option, ticketId: Number(ticketKey), qty });
        }
      }
    } else {
      // Walk productOptions rather than the quantity map: JS enumerates
      // integer-like keys in ascending numeric order, which would reorder the
      // per-order summary rows away from the server's option order.
      for (const option of product.productOptions) {
        const qty = product.optionQuantities?.[option.id] ?? 0;
        if (qty <= 0) continue;
        lines.push({ product, option, ticketId: null, qty });
      }
    }
  }

  return lines;
}

/** How many units of one option the whole order holds, across every ticket. */
export function getOrderWideOptionQty(
  product: StoreProduct,
  optionId: number,
): number {
  if (!product.isPerTicket) return product.optionQuantities?.[optionId] ?? 0;

  return Object.values(product.ticketOptionQuantities ?? {}).reduce(
    (sum, optionQuantities) => sum + (optionQuantities[optionId] ?? 0),
    0,
  );
}

/** How many units of a product the whole order holds, across every option. */
export function getProductTotalQty(product: StoreProduct): number {
  if (!product.isPerTicket) {
    return Object.values(product.optionQuantities ?? {}).reduce(
      (sum, qty) => sum + qty,
      0,
    );
  }

  return Object.values(product.ticketOptionQuantities ?? {}).reduce(
    (sum, optionQuantities) =>
      sum + Object.values(optionQuantities).reduce((s, qty) => s + qty, 0),
    0,
  );
}

/**
 * Quantity held by one scope — a single ticket when `ticketId` is given, the
 * order otherwise.
 */
export function getScopedQty(
  product: StoreProduct,
  optionId: number,
  ticketId?: number,
): number {
  if (ticketId === undefined) return product.optionQuantities?.[optionId] ?? 0;
  return product.ticketOptionQuantities?.[ticketId]?.[optionId] ?? 0;
}

/** How many units of a product one ticket holds, across every option. */
export function getTicketProductQty(
  product: StoreProduct,
  ticketId: number,
): number {
  return Object.values(product.ticketOptionQuantities?.[ticketId] ?? {}).reduce(
    (sum, qty) => sum + qty,
    0,
  );
}

/**
 * Units of an option left in the shared stock pool. Stock is order-wide for
 * both kinds of product — what ticket 1 takes, ticket 2 cannot — so this is
 * deliberately NOT scoped to a ticket.
 */
export function getStockHeadroom(
  product: StoreProduct,
  option: ProductOption,
): number {
  return Math.max(
    0,
    option.quantity - getOrderWideOptionQty(product, option.id),
  );
}

/**
 * The most units of an option a single ticket may hold. Per-ticket products
 * only: for them maximumOrder caps each ticket, not the order.
 */
export function getTicketCeiling(option: ProductOption): number {
  return option.maximumOrder > 0 ? option.maximumOrder : Infinity;
}

/** Stock the shop still has for a product, across every option, ignoring this cart. */
export function getProductStockCapacity(product: StoreProduct): number {
  return product.productOptions.reduce((sum, o) => sum + Math.max(0, o.quantity), 0);
}

/**
 * The fewest units one ticket must hold to satisfy the compulsory minimum.
 *
 * Normally that is the minimum itself, but an option that cannot be bought in
 * ones raises the price of satisfying a ticket: a minimum of 2 bought through an
 * option with `minimumOrder: 3` costs 3, not 2. Taking the cheapest option is
 * right because the customer may use whichever one they like.
 */
export function getTicketSatisfactionCost(product: StoreProduct): number {
  const required = product.minimumQuantity ?? 0;
  if (required <= 0) return 0;

  const costs = product.productOptions
    .filter((o) => o.quantity > 0)
    .map((o) => Math.max(required, getMinQty(o)));

  return costs.length > 0 ? Math.min(...costs) : required;
}

/**
 * How many tickets the remaining stock could satisfy. The cart is expected to
 * satisfy at least this many — a shortfall is only excusable once scarcity, not
 * the distribution of the cart, is what is causing it.
 */
export function getMaxSatisfiableTickets(
  product: StoreProduct,
  ticketCount: number,
): number {
  const cost = getTicketSatisfactionCost(product);
  if (cost <= 0) return 0;
  return Math.min(ticketCount, Math.floor(getProductStockCapacity(product) / cost));
}

/**
 * Units of a compulsory product one ticket may still take, once the stock other
 * tickets need to reach the minimum is set aside for them.
 *
 * Without this, one ticket can drain the pool and every other ticket is then
 * "excused" for having nothing — which is the compulsory rule being opted out
 * of, not scarcity. Reaching your OWN minimum is never withheld; only units
 * beyond it are, and only while another ticket that could still be satisfied
 * has not been.
 *
 * Stock that cannot satisfy anybody is not reserved: with 1 unit left against a
 * minimum of 2 there is no ticket to save it for, so it stays spendable.
 */
export function getTicketAddAllowance(
  product: StoreProduct,
  ticketId: number,
  orderedTicketIds: number[],
): number {
  const required = product.minimumQuantity ?? 0;
  if (required <= 0) return Infinity;

  const pool = product.productOptions.reduce(
    (sum, o) => sum + getStockHeadroom(product, o),
    0,
  );

  const ownNeed = Math.max(0, required - getTicketProductQty(product, ticketId));
  const afterOwnNeed = Math.max(0, pool - ownNeed);

  const otherNeeds = orderedTicketIds
    .filter((id) => id !== ticketId)
    .map((id) => Math.max(0, required - getTicketProductQty(product, id)))
    .filter((need) => need > 0)
    .sort((a, b) => a - b);

  // Smallest needs first, so the reservation covers as many tickets as the
  // stock can actually serve rather than hoarding for one it cannot.
  let remaining = afterOwnNeed;
  let reserved = 0;
  for (const need of otherNeeds) {
    if (need > remaining) break;
    reserved += need;
    remaining -= need;
  }

  return ownNeed + Math.max(0, afterOwnNeed - reserved);
}

/**
 * The ceiling a spinner may not go above for one scope — a single ticket when
 * `ticketId` is given, the order otherwise.
 *
 * Per-order: the order-wide ceiling, unchanged. Per-ticket: whatever is left in
 * the shared pool, but never past this ticket's own maximumOrder cap.
 *
 * `orderedTicketIds` additionally holds back stock the other tickets need for a
 * compulsory minimum (see getTicketAddAllowance). Callers asking "could this
 * ticket add anything at all?" leave it out on purpose: reaching your own
 * minimum is never reserved against, and feeding the reservation back into that
 * question would make it answer itself.
 */
export function getScopedMaxQty(
  product: StoreProduct,
  option: ProductOption,
  ticketId?: number,
  orderedTicketIds?: number[],
): number {
  const scopedQty = getScopedQty(product, option.id, ticketId);

  if (!product.isPerTicket) {
    return (
      getMaxAllowedQty(option) -
      getOrderWideOptionQty(product, option.id) +
      scopedQty
    );
  }

  const stockCeiling = Math.min(
    scopedQty + getStockHeadroom(product, option),
    getTicketCeiling(option),
  );

  if (ticketId === undefined || !orderedTicketIds) return stockCeiling;

  return Math.min(
    stockCeiling,
    scopedQty + getTicketAddAllowance(product, ticketId, orderedTicketIds),
  );
}

/**
 * The floor a spinner may not go below for one scope.
 *
 * Per-ticket: the option minimum, flat — the rule is per ticket, so a ticket
 * either holds none of the option or at least the minimum.
 *
 * Per-order: the option minimum only while this scope is the last one holding
 * the option, otherwise 1. That is the order-wide reading of the same field and
 * is kept verbatim for per-order products.
 */
export function getScopedMinQty(
  product: StoreProduct,
  option: ProductOption,
  ticketId?: number,
): number {
  if (product.isPerTicket) return getMinQty(option);

  const scopedQty = getScopedQty(product, option.id, ticketId);
  const isLastScopeHoldingOption =
    getOrderWideOptionQty(product, option.id) === scopedQty;
  return isLastScopeHoldingOption ? getMinQty(option) : 1;
}

/**
 * Can this ticket legally place one more unit of this option?
 *
 * Room alone is not enough — the unit has to land on a legal quantity. An option
 * with 1 unit left in the pool and a minimum of 2 cannot be added by anyone, so
 * counting it as addable would report a shortfall the customer can never clear:
 * the Add button is already disabled and the spinner floor is the minimum, so
 * there is no move that fixes it and checkout is blocked for good.
 *
 * Mirrored by ProductOptionListItem.CanAddMore in ProductOptionListItem.vb.
 */
export function canAddMoreOfOption(
  product: StoreProduct,
  option: ProductOption,
  ticketId: number,
): boolean {
  const scopedQty = getScopedQty(product, option.id, ticketId);
  return (
    getScopedMaxQty(product, option, ticketId) >=
    Math.max(getMinQty(option), scopedQty + 1)
  );
}

/**
 * Every ticket that falls short of a per-ticket product's minimums and could
 * still do something about it. Feeds the submit guard; the server check is its
 * mirror image.
 *
 * Per-order products are deliberately excluded — their minimums are enforced
 * server-side exactly as before, and a client pre-check would divert a path
 * that is meant to stay untouched.
 */
export function getMinimumViolations(
  products: StoreProduct[],
  orderedTicketIds: number[],
): MinimumViolation[] {
  const violations: MinimumViolation[] = [];

  for (const product of products) {
    if (!product.isPerTicket) continue;

    // Compulsory product minimum: every ticket must hold it, whether or not the
    // customer ever touched the product. The cart has to satisfy as many tickets
    // as the stock allows — so a shortfall is excused only once scarcity is what
    // is causing it, never the way the cart is distributed. Piling the pool onto
    // one ticket leaves the others short by choice, and that is a rejection.
    const required = product.minimumQuantity ?? 0;
    if (required > 0) {
      const satisfied = orderedTicketIds.filter(
        (id) => getTicketProductQty(product, id) >= required,
      ).length;

      if (satisfied < getMaxSatisfiableTickets(product, orderedTicketIds.length)) {
        for (const ticketId of orderedTicketIds) {
          const held = getTicketProductQty(product, ticketId);
          if (held < required) {
            violations.push({ product, ticketId, required, held });
          }
        }
        continue;
      }
    }

    for (const ticketId of orderedTicketIds) {
      // Conditional option minimum: only a ticket already holding the option
      // owes anything, and only while THAT option could still take a unit —
      // another option having stock does nothing for this shortfall. The
      // server scopes it per option too; widening it here would block carts
      // the server accepts, with a message pointing at no possible fix.
      for (const option of product.productOptions) {
        const optionQty = getScopedQty(product, option.id, ticketId);
        const optionMin = getMinQty(option);
        if (
          optionQty > 0 &&
          optionQty < optionMin &&
          canAddMoreOfOption(product, option, ticketId)
        ) {
          violations.push({
            product,
            ticketId,
            required: optionMin,
            held: optionQty,
          });
          break;
        }
      }
    }
  }

  return violations;
}

/** ticketId -> 1-based ticket number, in the order the server delivered them. */
export function getTicketNumberMap(
  orderedTicketIds: number[],
): Map<number, number> {
  return new Map(orderedTicketIds.map((id, index) => [id, index + 1]));
}
